import { useEffect, useState } from 'react';
import Text from 'src/components/Text/Text';
import UI_UTILS from 'src/utils/uiUtils';
import { UserStatus, ZERO } from '@drift-labs/sdk';
import Tooltip from '../Tooltip/Tooltip';
import { Info } from '@drift-labs/icons';
import MarginUpdateRow from './MarginUpdateRow';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';

const MarginLeverageSettings = (props: {
	addToMarginRatioUpdates: (
		newMarginRatio: number,
		subAccountId: number
	) => void;
	addToMarginTradingUpdates: (
		marginTradingEnabled: boolean,
		subAccountId: number
	) => void;
	addToDlpAdvancedUpdates: (advancedLp: boolean, subAccountId: number) => void;
}) => {
	const subAccounts = useDriftAccountStore((s) => s.accounts);
	const [subAccountMargins, setSubAccountMargins] = useState([]);

	const fetchSubAccountInfos = () => {
		setSubAccountMargins(
			Object.values(subAccounts).map((acct) => {
				if (!acct.client.isSubscribed) return;

				const customMarginRatio = acct.client.getUserAccount().maxMarginRatio;

				const maxLeverage =
					UI_UTILS.convertMarginRatioToLeverage(customMarginRatio);

				return {
					accountId: acct.userId,
					accountName: acct.isDelegatedTo
						? UI_UTILS.getDelegateAccountLabel(acct)
						: acct.name,
					marginEnabled: acct.client.getUserAccount().isMarginTradingEnabled,
					maxLeverage,
					isDelegate: acct.isDelegatedTo ?? false,
					accountHasLp: acct.marginInfo?.quoteInLpOrders?.gt(ZERO),
					advancedLp: acct.client.hasStatus(UserStatus.ADVANCED_LP),
				};
			})
		);
	};

	const setMarginEnabled = (accountId: number, newValue: boolean) =>
		props.addToMarginTradingUpdates(
			newValue ===
				subAccountMargins.find((subAcct) => subAcct.accountId === accountId)
					.marginEnabled
				? undefined
				: newValue,
			accountId
		);

	const setMarginRatio = (accountId: number, newValue: number | undefined) =>
		props.addToMarginRatioUpdates(
			newValue ===
				subAccountMargins.find((subAcct) => subAcct.accountId === accountId)
					.maxLeverage
				? -1
				: newValue,
			accountId
		);

	const setDlpAdvancedFlag = (accountId: number, newValue: boolean) => {
		props.addToDlpAdvancedUpdates(
			newValue ===
				subAccountMargins.find((subAcct) => subAcct.accountId === accountId)
					.advancedLp
				? undefined
				: newValue,
			accountId
		);
	};

	useEffect(() => {
		fetchSubAccountInfos();
	}, [subAccounts]);

	return (
		<div className="flex flex-col">
			<div className="mb-2">
				<Text.BODY1 className="text-text-emphasis">
					Margin/Leverage Settings
				</Text.BODY1>
			</div>
			<div>
				<Text.BODY2 className="text-text-secondary">
					You can customize your margin and leverage settings per subaccount.
				</Text.BODY2>
			</div>
			<div>
				<div className="flex grid items-center w-full grid-cols-4 mt-4 mb-2 space-x-1">
					<Text.BODY2 className="col-span-1 text-text-label">
						Subaccount
					</Text.BODY2>
					<Text.BODY2 className="col-span-1 text-text-label">Margin</Text.BODY2>
					<Tooltip
						className="items-center col-span-1"
						content="This is the Max Initial Margin Allowed based on weighted assets/liabilities"
					>
						<Text.BODY2 className="mr-1 text-text-label">
							Max. Leverage
						</Text.BODY2>
						<Info color="var(--text-label)" size={12} />
					</Tooltip>
					<Tooltip
						className="items-center justify-end col-span-1"
						content="If disabled, BAL positions will NOT auto de-risk (on next P&L settle) if initial margin requirement is not met. Recommended for advanced users only."
					>
						<Text.BODY2 className="text-text-label border-b border-dashed border-input-border mr-0.5">
							Auto-Derisk BAL
						</Text.BODY2>
						<Info color="var(--text-label)" size={12} />
					</Tooltip>
				</div>
				{subAccountMargins
					.filter((subAcct) => !subAcct.isDelegate)
					.map((subAcct) => (
						<MarginUpdateRow
							spacedOut={true}
							key={`${subAcct.accountName}_margin`}
							label={subAcct.accountName}
							marginEnabledChecked={subAcct.marginEnabled}
							currentMaxLev={subAcct.maxLeverage}
							// use the opposite of contract flag for ui so it shows on by default
							dlpAutoDerisk={!subAcct.advancedLp}
							onChangeEnabled={(newValue: boolean) => {
								setMarginEnabled(subAcct.accountId, newValue);
							}}
							onChangeRatio={(newValue: number | undefined) => {
								setMarginRatio(subAcct.accountId, newValue);
							}}
							onChangeDlpAutoDerisk={(newValue: boolean) => {
								// use the opposite of contract flag for ui so it shows on by default
								setDlpAdvancedFlag(subAcct.accountId, !newValue);
							}}
							disabled={false}
						/>
					))}
				{subAccountMargins.filter((subAcct) => subAcct.isDelegate)?.length >
					0 && (
					<div className="flex grid items-center w-full grid-cols-4 mt-4 mb-2">
						<Text.BODY2 className="col-span-1 text-text-label">
							Delegated accounts
						</Text.BODY2>
						<Text.BODY2 className="col-span-1 text-text-label">
							Margin
						</Text.BODY2>
						<Tooltip
							className="items-center col-span-1"
							content="This is the Max Initial Margin Allowed based on weighted assets/liabilities"
						>
							<Text.BODY2 className="text-text-label mr-0.5">
								Max. Leverage
							</Text.BODY2>
							<Info color="var(--text-label)" size={12} />
						</Tooltip>
						<Tooltip
							className="items-center justify-end col-span-1 border-b border-dashed border-input-border"
							content="If disabled, BAL positions will NOT auto de-risk (on next P&L settle) if initial margin requirement is not met. Recommended for advanced users only."
						>
							<Text.BODY2 className="text-text-label mr-0.5">
								Auto-Derisk BAL
							</Text.BODY2>
							<Info color="var(--text-label)" size={12} />
						</Tooltip>
					</div>
				)}
				{subAccountMargins
					.filter((subAcct) => subAcct.isDelegate)
					.map((subAcct) => (
						<MarginUpdateRow
							spacedOut={true}
							key={`${subAcct.accountName}_margin`}
							label={subAcct.accountName}
							marginEnabledChecked={subAcct.marginEnabled}
							currentMaxLev={subAcct.maxLeverage}
							dlpAutoDerisk={!subAcct.advancedLp}
							onChangeEnabled={(_newValue: boolean) => {
								// disabled
							}}
							onChangeRatio={(_newValue: number | undefined) => {
								// disabled
							}}
							onChangeDlpAutoDerisk={(_newValue: boolean) => {
								// disabled
							}}
							disabled={true}
						/>
					))}
			</div>
		</div>
	);
};

export default MarginLeverageSettings;
